'use client';

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { ShieldAlert, CheckCircle2 } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { getRecaptchaToken } from '@/lib/recaptcha';

interface SubmitResult {
  id: number;
  status: string;
  slaDeadline: string;
  message: string;
}

const RELATIONSHIP_OPTIONS: Array<{ value: string; label: string; hint?: string }> = [
  { value: 'depicted_person', label: 'I am the person depicted in the content' },
  { value: 'legal_representative', label: 'I am the legal representative of the depicted person', hint: 'Attorney, agent, or authorized representative' },
  { value: 'guardian_of_minor', label: 'I am the parent or legal guardian of a minor depicted', hint: 'Reports about minors are escalated immediately' },
  { value: 'other', label: 'Other', hint: 'Briefly explain in the description' },
];

function isLikelyEmail(s: string): boolean {
  const t = s.trim();
  return t.includes('@') && t.includes('.') && !t.includes(' ') && t.length <= 254;
}

export default function RemovalRequestPage() {
  const [contentUrl, setContentUrl] = useState('');
  const [reporterName, setReporterName] = useState('');
  const [reporterEmail, setReporterEmail] = useState('');
  const [reporterRelationship, setReporterRelationship] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SubmitResult | null>(null);

  function validate(): string | null {
    if (!contentUrl.trim()) return 'Please paste the URL of the content you want removed.';
    if (contentUrl.length > 2048) return 'Content URL is too long.';
    if (!reporterName.trim()) return 'Please enter your name.';
    if (reporterName.length > 200) return 'Name is too long.';
    if (!reporterEmail.trim() || !isLikelyEmail(reporterEmail)) return 'Please enter a valid email address.';
    if (!reporterRelationship) return 'Please tell us your relationship to the depicted person.';
    if (!description.trim()) return 'Please describe what you want removed and why.';
    if (description.length > 4000) return 'Description is too long (4000 character maximum).';
    return null;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setSubmitting(true);
    try {
      // Bot signal for the server (see RemovalRequestController). A low score
      // NEVER blocks a takedown — the server only uses it to flag likely-spam and
      // skip the reporter-confirmation email. Empty string on failure is fine.
      const recaptchaToken = await getRecaptchaToken('removal');
      const resp = await apiFetch('/api/report/removal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contentUrl: contentUrl.trim(),
          reporterName: reporterName.trim(),
          reporterEmail: reporterEmail.trim().toLowerCase(),
          reporterRelationship,
          description: description.trim(),
          recaptchaToken,
        }),
      });
      if (!resp.ok && resp.status !== 202) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body.error || `Submit failed (${resp.status})`);
      }
      const data = (await resp.json()) as SubmitResult;
      setResult(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Submit failed. Please try again or email compliance@juicystarz.com directly.');
    } finally {
      setSubmitting(false);
    }
  }

  if (result) {
    const sla = (() => {
      try { return new Date(result.slaDeadline).toLocaleString(); } catch { return result.slaDeadline; }
    })();
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
            <div className="p-6 border-b border-gray-200 flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">Request received</h1>
                <p className="text-sm text-gray-600 mt-1">Reference: <span className="font-mono">REM-{result.id}</span></p>
              </div>
            </div>
            <div className="p-6 space-y-4 text-sm text-gray-700">
              <p>{result.message}</p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="font-semibold text-amber-900">Response deadline</p>
                <p className="text-amber-800 mt-1">We will respond by <span className="font-semibold">{sla}</span> (within 48 hours of submission, per the federal Take It Down Act).</p>
              </div>
              <p>
                A confirmation email has been sent to your address. If you don&apos;t see it within an hour, check spam or contact{' '}
                <a className="text-purple-700 hover:underline" href="mailto:compliance@juicystarz.com">compliance@juicystarz.com</a>{' '}
                with your reference number.
              </p>
              <p className="text-xs text-gray-500 pt-2 border-t border-gray-200">
                For trafficking, exploitation, or content involving a minor that needs immediate action, also contact the National Center for Missing &amp; Exploited Children at <span className="font-semibold">1-800-843-5678</span> or <a className="text-purple-700 hover:underline" href="https://report.cybertip.org" target="_blank" rel="noopener noreferrer">CyberTipline</a>.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
          {/* Header */}
          <div className="p-6 border-b border-gray-200 flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
              <ShieldAlert className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">Report content for removal</h1>
              <p className="text-sm text-gray-600 mt-1">
                For non-consensual intimate imagery (NCII), right-of-publicity claims, and other removal requests.
                We respond within 48 hours per the federal Take It Down Act.
              </p>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {/* Content URL */}
            <div>
              <label htmlFor="rem-url" className="block text-sm font-semibold text-gray-800 mb-1">
                URL of the content <span className="text-red-600">*</span>
              </label>
              <input
                id="rem-url"
                type="url"
                value={contentUrl}
                onChange={(e) => setContentUrl(e.target.value)}
                placeholder="https://juicystarz.com/gallery/..."
                required
                disabled={submitting}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Copy the full link from your browser&apos;s address bar. If multiple pieces of content are involved, file a separate request for each.
              </p>
            </div>

            {/* Relationship */}
            <fieldset>
              <legend className="block text-sm font-semibold text-gray-800 mb-1">
                Your relationship to the depicted person <span className="text-red-600">*</span>
              </legend>
              <div className="space-y-2 mt-1">
                {RELATIONSHIP_OPTIONS.map((opt) => (
                  <label key={opt.value} className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="relationship"
                      value={opt.value}
                      checked={reporterRelationship === opt.value}
                      onChange={(e) => setReporterRelationship(e.target.value)}
                      disabled={submitting}
                      className="mt-1 h-4 w-4 text-purple-600"
                    />
                    <span className="text-sm">
                      <span className="text-gray-800">{opt.label}</span>
                      {opt.hint && <span className="block text-xs text-gray-500">{opt.hint}</span>}
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>

            {/* Reporter name + email */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="rem-name" className="block text-sm font-semibold text-gray-800 mb-1">
                  Your full name <span className="text-red-600">*</span>
                </label>
                <input
                  id="rem-name"
                  type="text"
                  value={reporterName}
                  onChange={(e) => setReporterName(e.target.value)}
                  required
                  disabled={submitting}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div>
                <label htmlFor="rem-email" className="block text-sm font-semibold text-gray-800 mb-1">
                  Contact email <span className="text-red-600">*</span>
                </label>
                <input
                  id="rem-email"
                  type="email"
                  value={reporterEmail}
                  onChange={(e) => setReporterEmail(e.target.value)}
                  required
                  disabled={submitting}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>

            {/* Description */}
            <div>
              <label htmlFor="rem-desc" className="block text-sm font-semibold text-gray-800 mb-1">
                Why should this be removed? <span className="text-red-600">*</span>
              </label>
              <textarea
                id="rem-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={6}
                maxLength={4000}
                required
                disabled={submitting}
                placeholder="Describe the content and the reason for the removal request. Include dates, locations, or any other context that helps us verify your claim."
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                {description.length} / 4000
              </p>
            </div>

            {/* Penalty notice */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
              <p className="font-semibold mb-1">Important</p>
              <p>
                Submitting a false removal claim is a federal offense under 18 U.S.C. § 1001 and may result in
                liability under state false-reporting statutes. We retain submission metadata (IP, timestamp,
                browser) for legal-process purposes.
              </p>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            <div className="flex flex-col gap-2">
              <Button type="submit" variant="primary" className="w-full" disabled={submitting}>
                {submitting ? 'Submitting…' : 'Submit removal request'}
              </Button>
              <p className="text-xs text-gray-500 text-center">
                Need to report trafficking, exploitation, or a minor in immediate danger?{' '}
                <a className="text-purple-700 hover:underline" href="/report">Use the urgent report page instead.</a>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
